/**
 * Error utilities for Authentication Service
 * REG-007: Service Layer Implementation
 */

import { AuthError } from './auth.types';
import { AuthError as SupabaseAuthError } from '@supabase/supabase-js';

/**
 * Error messages mapped from Supabase to user-friendly messages
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors
  'auth/email-already-in-use': 'This email is already registered. Please sign in or use password reset.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/weak-password': 'Password must be at least 12 characters with mixed case, numbers, and symbols.',
  'auth/operation-not-allowed': 'Registration is temporarily unavailable. Please try again later.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed': 'Connection error. Please check your internet and try again.',
  'auth/user-not-found': 'No account found with this email.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/email-not-verified': 'Please verify your email before continuing.',
  'User already registered': 'An account with this email already exists. Try signing in instead.',
  'Invalid login credentials': 'Invalid email or password. Please try again.',
  
  // Rate limiting
  'rate-limit-exceeded': 'Please wait 60 seconds before resending verification email.',
  'Email rate limit exceeded': 'Too many email requests. Please wait a moment and try again.',
  
  // Company errors
  'company-duplicate': 'A company with this name already exists. Please choose a different name.',
  
  // Token errors
  'token-expired': 'Your verification link has expired. Please request a new one.',
  'token-invalid': 'Invalid verification link. Please check your email and try again.',
  'expired': 'Your verification link has expired. Please request a new one.',
  'invalid': 'Invalid verification link. Please check your email and try again.',
  
  // Generic errors
  'server-error': 'A server error occurred. Please try again later.',
  'validation-error': 'Please check all fields and try again.',
  'network-error': 'Connection error. Please check your internet and try again.',
};

/**
 * Map Supabase error to AuthError with user-friendly message
 */
export function mapSupabaseError(error: unknown): AuthError {
  // Handle Supabase auth errors
  if (error && typeof error === 'object' && 'message' in error) {
    const authError = error as SupabaseAuthError;
    const message = authError.message || 'An unexpected error occurred';
    
    // Find matching error message
    const userMessage = Object.entries(ERROR_MESSAGES).find(([key]) => 
      message.toLowerCase().includes(key.toLowerCase())
    )?.[1] || ERROR_MESSAGES['server-error'];
    
    return {
      code: authError.status?.toString() || 'UNKNOWN',
      message,
      userMessage,
      details: { originalError: authError }
    };
  }
  
  // Handle generic errors
  if (error instanceof Error) {
    const userMessage = ERROR_MESSAGES[error.message] || ERROR_MESSAGES['server-error'];
    
    return {
      code: 'ERROR',
      message: error.message,
      userMessage,
      details: { originalError: error }
    };
  }
  
  // Unknown error type
  return {
    code: 'UNKNOWN',
    message: 'An unexpected error occurred',
    userMessage: ERROR_MESSAGES['server-error'],
    details: { originalError: error }
  };
}

/**
 * Check if error is a client error (4xx) that should not be retried
 */
export function isClientError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'status' in error) {
    const status = (error as { status: number }).status;
    return status >= 400 && status < 500;
  }
  return false;
}

/**
 * Check if error is a network error that can be retried
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof Error) {
    return error.message.toLowerCase().includes('network') ||
           error.message.toLowerCase().includes('fetch') ||
           error.message.toLowerCase().includes('connection');
  }
  return false;
}

/**
 * Check if error is rate limit error
 */
export function isRateLimitError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: string }).message.toLowerCase();
    return message.includes('rate limit') || 
           message.includes('too many') ||
           message.includes('429');
  }
  return false;
}
